/**
 * Primer ingreso al Vault (F3a) — tres pasos, con el copy HONESTO de la Opción A:
 * la passphrase es un segundo candado, no la llave de cifrado; el servidor puede
 * descifrar; IT puede resetearla (con aviso y en el log).
 *
 * El mockup original decía "Winclap no la guarda ni puede verla" — eso sería
 * falso con KMS y el doc (§6.5) lo prohíbe explícitamente.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { CheckCircle2, KeyRound, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { errMsg, useVaultMutations } from '@/hooks/useVault';
import { checkPwnedPassphrase, passphraseStrength, type PwnedResult } from '@/lib/hibp';
import { VAULT_POLICY } from '../../../../shared/types/vault';

const MIN = VAULT_POLICY.passphraseMinLength;

export function PassphraseSetupWizard({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setupPassphrase } = useVaultMutations();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [passphrase, setPassphrase] = useState('');
  const [confirm, setConfirm] = useState('');
  const [ack, setAck] = useState(false);
  const [pwned, setPwned] = useState<PwnedResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const strength = passphraseStrength(passphrase);
  const strengthLabel = {
    muy_debil: t('vault.setup.strength_0', 'Muy débil'),
    debil: t('vault.setup.strength_1', 'Débil'),
    aceptable: t('vault.setup.strength_2', 'Aceptable'),
    fuerte: t('vault.setup.strength_3', 'Fuerte'),
    muy_fuerte: t('vault.setup.strength_4', 'Muy fuerte'),
  }[strength.label];

  // HIBP con k-anonymity, con debounce: solo el prefijo del hash sale del navegador.
  useEffect(() => {
    if (passphrase.length < MIN) {
      setPwned(null);
      return;
    }
    const id = window.setTimeout(() => {
      checkPwnedPassphrase(passphrase).then(setPwned);
    }, 500);
    return () => window.clearTimeout(id);
  }, [passphrase]);

  const isPwned = pwned?.checked && pwned.count > 0;
  const canContinue = passphrase.length >= MIN && passphrase === confirm && ack && !isPwned && strength.score >= 2;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await setupPassphrase.mutateAsync({ passphrase });
      setStep(3);
    } catch (err) {
      setError(errMsg(err as { message?: string }));
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4">
      <Card className="w-full max-w-xl p-8">
        <div className="mb-6 flex items-center gap-2">
          {[1, 2, 3].map((n, i) => (
            <div key={n} className="flex flex-1 items-center gap-2">
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${step >= n ? 'bg-brand-green text-white' : 'bg-muted text-muted-foreground'}`}>{n}</span>
              {i < 2 && <span className={`h-px flex-1 ${step > n ? 'bg-brand-green' : 'bg-border'}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/30">
                <KeyRound className="h-5 w-5" />
              </div>
              <div>
                <h1 className="font-display text-2xl">{t('vault.setup.intro_title', 'Tu passphrase del Vault')}</h1>
                <p className="text-sm text-muted-foreground">{t('vault.setup.intro_sub', 'Antes de crearla, esto es lo que hace y lo que no.')}</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm">
              <li className="rounded-md border p-3">
                <strong>{t('vault.setup.what_title', 'Qué es.')}</strong>{' '}
                {t('vault.setup.what_body', 'Un segundo candado para abrir credenciales dentro del Vault, distinto de tu contraseña de Google. Evita que alguien con tu sesión de Internal abierta pueda ver tus secretos.')}
              </li>
              <li className="rounded-md border p-3">
                <strong>{t('vault.setup.not_title', 'Qué no es.')}</strong>{' '}
                {t('vault.setup.not_body', 'No cifra los datos. Las credenciales están cifradas con AWS KMS y el servidor de Internal puede descifrarlas: el Vault protege contra sesiones abiertas y accesos indebidos, no contra un servidor comprometido.')}
              </li>
              <li className="rounded-md border p-3">
                <strong>{t('vault.setup.lost_title', 'Si la perdés.')}</strong>{' '}
                {t('vault.setup.lost_body', 'Un admin de IT la resetea: te avisamos por Slack y queda en el registro de auditoría. Ninguna credencial se pierde.')}
              </li>
            </ul>
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} data-tour="setup-continue">{t('vault.setup.continue', 'Continuar')}</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h1 className="font-display text-2xl">{t('vault.setup.create_title', 'Creá tu passphrase')}</h1>
              <p className="text-sm text-muted-foreground">
                {t('vault.setup.create_sub', 'Mínimo {{min}} caracteres. Cuatro palabras con separadores funcionan mejor que una contraseña corta con símbolos.', { min: MIN })}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pp">{t('vault.setup.label', 'Passphrase')}</Label>
              <Input id="pp" type="password" autoComplete="new-password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} autoFocus />
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full transition-all ${strength.score >= 3 ? 'bg-brand-green' : strength.score === 2 ? 'bg-amber-400' : 'bg-rose-400'}`}
                  style={{ width: `${(strength.score / 4) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {passphrase ? strengthLabel : t('vault.setup.strength_hint', 'La verificamos contra filtraciones conocidas sin enviarla (solo un prefijo de su hash).')}
                {isPwned && <span className="ml-1 text-destructive">{t('vault.setup.pwned', '· Esta passphrase apareció en filtraciones; elegí otra.')}</span>}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pp2">{t('vault.setup.confirm', 'Repetir passphrase')}</Label>
              <Input id="pp2" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              {confirm && confirm !== passphrase && <p className="text-xs text-destructive">{t('vault.setup.mismatch', 'No coinciden.')}</p>}
            </div>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm">
              <Checkbox checked={ack} onCheckedChange={(v) => setAck(v === true)} className="mt-0.5" data-tour="setup-ack" />
              <span>{t('vault.setup.ack', 'Entiendo que la passphrase no cifra los datos, que el servidor puede descifrarlos, y que IT puede resetearla con aviso.')}</span>
            </label>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => setStep(1)}>{t('vault.setup.back', 'Volver')}</Button>
              <Button type="submit" disabled={!canContinue || setupPassphrase.isPending} data-tour="setup-create">
                {setupPassphrase.isPending ? t('vault.setup.saving', 'Guardando…') : t('vault.setup.create_cta', 'Crear passphrase')}
              </Button>
            </div>
          </form>
        )}

        {step === 3 && (
          <div className="space-y-4 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-brand-green" />
            <h1 className="font-display text-2xl">{t('vault.setup.done_title', 'Listo')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('vault.setup.done_body', 'El Vault queda desbloqueado 15 minutos en esta pestaña. Después, te va a pedir la passphrase de nuevo.')}
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4" />
              {t('vault.setup.done_audit', 'Este primer ingreso quedó registrado en la auditoría.')}
            </div>
            <Button onClick={() => { onDone(); navigate('/vault'); }}>{t('vault.setup.go', 'Ir a mis credenciales')}</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
